"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from "react";
import { useXtdhTokensQuery } from "@/hooks/useXtdhTokensQuery";
import { useXtdhTokensFilters } from "../hooks/useXtdhTokensFilters";
import { XtdhTokensControls } from "../tokens-controls";
import { XtdhReceivedCollectionCard } from "../collection-card-content";
import { useCollectionContractDetails } from "./hooks/useCollectionContractDetails";
import { useXtdhTokenSelection } from "./hooks/useXtdhTokenSelection";
import { XtdhTokensList } from "./XtdhTokensList";
import { CollectionBreadcrumbs } from "./subcomponents/CollectionBreadcrumbs";
import { CollectionLoadMore } from "./subcomponents/CollectionLoadMore";
import { XtdhTokenListItem } from "./subcomponents/XtdhTokenListItem";
import { XtdhTokenContributorsPanel } from "./token-contributors";
import type { XtdhCollectionTokensPanelProps } from "./types";
import { getTokenLabel } from "./utils/getTokenLabel";
const TOKENS_PAGE_SIZE = 25;
export function XtdhCollectionTokensPanel(t0) {
  const $ = _c(57);
  const {
    identity,
    contract,
    normalizedContract,
    collection,
    onBack,
    requireIdentity: t1
  } = t0;
  const requireIdentity = t1 === undefined ? true : t1;
  const {
    activeSortField,
    activeSortDirection,
    apiOrder,
    handleSortChange
  } = useXtdhTokensFilters();
  const {
    selectedToken,
    selectToken,
    clearSelectedToken
  } = useXtdhTokenSelection();
  const [selectedGrant, setSelectedGrant] = useState(null);
  let t2;
  if ($[0] !== contract || $[1] !== normalizedContract) {
    t2 = {
      contract,
      normalizedContract
    };
    $[0] = contract;
    $[1] = normalizedContract;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    contractParam,
    normalizedAddress,
    contractDisplayName
  } = useCollectionContractDetails(t2);
  let t3;
  if ($[3] !== activeSortField || $[4] !== apiOrder || $[5] !== contractParam || $[6] !== identity || $[7] !== requireIdentity) {
    t3 = {
      identity,
      contract: contractParam,
      pageSize: TOKENS_PAGE_SIZE,
      sortField: activeSortField,
      order: apiOrder,
      requireIdentity
    };
    $[3] = activeSortField;
    $[4] = apiOrder;
    $[5] = contractParam;
    $[6] = identity;
    $[7] = requireIdentity;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    tokens,
    isLoading,
    isError,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    fetchNextPage,
    errorMessage,
    isEnabled
  } = useXtdhTokensQuery(t3);
  let t4;
  if ($[9] !== refetch) {
    t4 = () => {
      refetch().catch(_temp);
    };
    $[9] = refetch;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleRetry = t4;
  let t5;
  if ($[11] !== fetchNextPage || $[12] !== hasNextPage || $[13] !== isFetchingNextPage) {
    t5 = () => {
      if (!hasNextPage || isFetchingNextPage) {
        return;
      }
      fetchNextPage().catch(_temp2);
    };
    $[11] = fetchNextPage;
    $[12] = hasNextPage;
    $[13] = isFetchingNextPage;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleLoadMore = t5;
  const controlsDisabled = isLoading || isFetching;
  const showLoadMore = hasNextPage && isEnabled;
  let t6;
  bb0: {
    if (!selectedToken) {
      t6 = undefined;
      break bb0;
    }
    let t7;
    if ($[15] !== selectedToken.metadata?.name || $[16] !== selectedToken.token) {
      t7 = selectedToken.metadata?.name ?? getTokenLabel(selectedToken.token.token);
      $[15] = selectedToken.metadata?.name;
      $[16] = selectedToken.token;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    t6 = t7;
  }
  const selectedTokenLabel = t6;
  let t7;
  if ($[18] !== clearSelectedToken) {
    t7 = () => {
      clearSelectedToken();
      setSelectedGrant(null);
    };
    $[18] = clearSelectedToken;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleBackToTokens = t7;
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setSelectedGrant(null);
    };
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleBackToContributors = t8;
  let t9;
  bb1: {
    if (!selectedToken) {
      t9 = null;
      break bb1;
    }
    let t10;
    if ($[21] !== selectedToken.token.token) {
      t10 = Number.isFinite(selectedToken.token.token) ? Math.trunc(selectedToken.token.token) : null;
      $[21] = selectedToken.token.token;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    t9 = t10;
  }
  const selectedTokenId = t9;
  const t10 = selectedGrant?.label;
  const t11 = selectedToken ? handleBackToTokens : undefined;
  const t12 = selectedGrant ? handleBackToContributors : undefined;
  let t13;
  if ($[23] !== contractDisplayName || $[24] !== onBack || $[25] !== selectedTokenLabel || $[26] !== t10 || $[27] !== t11 || $[28] !== t12) {
    t13 = <CollectionBreadcrumbs collectionLabel={contractDisplayName} tokenLabel={selectedTokenLabel} grantLabel={t10} onNavigateToCollections={onBack} onNavigateToTokens={t11} onNavigateToContributors={t12} />;
    $[23] = contractDisplayName;
    $[24] = onBack;
    $[25] = selectedTokenLabel;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== collection) {
    t14 = collection ? <XtdhReceivedCollectionCard collection={collection} interactionMode="static" /> : <div className="tw-rounded-xl tw-border tw-border-iron-800 tw-bg-iron-900 tw-p-4 tw-text-sm tw-text-iron-300">Collection summary unavailable.</div>;
    $[30] = collection;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== activeSortDirection || $[33] !== activeSortField || $[34] !== contractParam || $[35] !== controlsDisabled || $[36] !== errorMessage || $[37] !== handleBackToTokens || $[38] !== handleLoadMore || $[39] !== handleRetry || $[40] !== handleSortChange || $[41] !== isEnabled || $[42] !== isError || $[43] !== isFetchingNextPage || $[44] !== isLoading || $[45] !== normalizedAddress || $[46] !== selectToken || $[47] !== selectedGrant || $[48] !== selectedToken || $[49] !== selectedTokenId || $[50] !== showLoadMore || $[51] !== tokens) {
    t15 = selectedToken && selectedTokenId !== null ? <div className="tw-space-y-4"><XtdhTokenListItem as="div" className="tw-p-4 tw-cursor-pointer tw-transition-all tw-duration-300 desktop-hover:hover:tw-bg-iron-950" token={selectedToken.token} metadata={selectedToken.metadata} isMetadataLoading={selectedToken.isMetadataLoading} hasMetadataError={selectedToken.hasMetadataError} onSelect={handleBackToTokens} />{!selectedGrant && <p className="tw-m-0 tw-text-sm tw-text-iron-300">Explore the grants and grantors powering this token's xTDH.</p>}<XtdhTokenContributorsPanel contract={contractParam} tokenId={selectedTokenId} selectedGrantId={selectedGrant?.id ?? null} onSelectGrant={(id, label) => setSelectedGrant({
        id,
        label
      })} /></div> : <><XtdhTokensControls activeSortField={activeSortField} activeSortDirection={activeSortDirection} onSortChange={handleSortChange} isDisabled={controlsDisabled} /><XtdhTokensList tokens={tokens} contractAddress={normalizedAddress} isEnabled={isEnabled} isLoading={isLoading} isError={isError} errorMessage={errorMessage} onRetry={handleRetry} onTokenSelect={selectToken} /><CollectionLoadMore isVisible={showLoadMore} isFetching={isFetchingNextPage} onLoadMore={handleLoadMore} /></>;
    $[32] = activeSortDirection;
    $[33] = activeSortField;
    $[34] = contractParam;
    $[35] = controlsDisabled;
    $[36] = errorMessage;
    $[37] = handleBackToTokens;
    $[38] = handleLoadMore;
    $[39] = handleRetry;
    $[40] = handleSortChange;
    $[41] = isEnabled;
    $[42] = isError;
    $[43] = isFetchingNextPage;
    $[44] = isLoading;
    $[45] = normalizedAddress;
    $[46] = selectToken;
    $[47] = selectedGrant;
    $[48] = selectedToken;
    $[49] = selectedTokenId;
    $[50] = showLoadMore;
    $[51] = tokens;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t13 || $[54] !== t14 || $[55] !== t15) {
    t16 = <section className="tw-rounded-xl tw-border tw-border-iron-800 tw-bg-iron-950 tw-space-y-4 tw-px-6 tw-pb-6">{t13}{t14}{t15}</section>;
    $[53] = t13;
    $[54] = t14;
    $[55] = t15;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  return t16;
}
function _temp2() {}
function _temp() {}